import * as yup from 'yup';

// Extend yup interface
declare module 'yup' {
    interface StringSchema {
        strongPassword(): StringSchema;
        validEmail(): StringSchema;
    }
}

yup.addMethod<yup.StringSchema>(yup.string, 'validEmail', function () {
    return this.email('Enter a valid email address');
});

yup.addMethod<yup.StringSchema>(yup.string, 'strongPassword', function () {
    return this
        .min(8, 'At least 8 characters')
        .matches(/[A-Z]/, 'Must contain an uppercase letter')
        .matches(/[a-z]/, 'Must contain a lowercase letter')
        .matches(/\d/, 'Must contain a number');
});

// Login form validation
export const loginSchema = yup.object().shape({
    email: yup
        .string()
        .required('Email is required')
        .validEmail(),
    password: yup
        .string()
        .required('Password is required')
        .min(1, 'Password is required'),
    rememberMe: yup
        .boolean()
        .defined()
        .default(false),
});

// Registration form validation
export const registerSchema = yup.object().shape({
    name: yup
        .string()
        .required('Full name is required')
        .min(2, 'Name must be at least 2 characters')
        .max(100, 'Name must be less than 100 characters')
        .matches(/^[a-zA-Z\s-']+$/, 'Name contains invalid characters'),
    email: yup
        .string()
        .required('Email is required')
        .validEmail(),
    password: yup
        .string()
        .required('Password is required')
        .strongPassword(),
    confirmPassword: yup
        .string()
        .required('Please confirm your password')
        .oneOf([yup.ref('password')], 'Passwords must match'),
    termsAccepted: yup
        .boolean()
        .oneOf([true], 'You must accept the terms and conditions'),
});

// Forgot password form validation
export const forgotPasswordSchema = yup.object().shape({
    email: yup
        .string()
        .required('Email is required')
        .validEmail(),
});

// Reset password form validation
export const resetPasswordSchema = yup.object().shape({
    newPassword: yup
        .string()
        .required('New password is required')
        .strongPassword(),
    confirmPassword: yup
        .string()
        .required('Please confirm your password')
        .oneOf([yup.ref('newPassword')], 'Passwords must match'),
});

// MFA code validation
export const mfaCodeSchema = yup.object().shape({
    code: yup
        .string()
        .required('Verification code is required')
        .matches(/^\d{6}$/, 'Code must be 6 digits')
        .length(6, 'Code must be exactly 6 digits'),
});

// Email verification token validation
export const emailVerificationSchema = yup.object().shape({
    token: yup
        .string()
        .required('Verification token is required')
        .min(10, 'Invalid verification token'),
});



// Define explicit types instead of using yup.InferType to avoid undefined issues
export interface LoginFormData {
    username: string;
    password: string;
    rememberMe: boolean;
}

export interface RegisterFormData {
    firstName: string;
    lastName: string;
    email: string;
    username: string;
    password: string;
    confirmPassword: string;
    termsAccepted: boolean;
}

export interface ForgotPasswordFormData {
    email: string;
}

export interface ResetPasswordFormData {
    newPassword: string;
    confirmPassword: string;
}

export interface MFACodeFormData {
    code: string;
}

export interface EmailVerificationFormData {
    token: string;
}

export interface GitHubConfigFormData {
    githubAppId: string;
    personalAccessToken: string;
    privateKey: File;
}

// Alternative: If you prefer to keep yup.InferType, you can use this approach
// export type LoginFormData = Required<yup.InferType<typeof loginSchema>>;
// export type RegisterFormData = Required<yup.InferType<typeof registerSchema>>;
// etc...

// Password strength helper function
export const getPasswordStrengthText = (password: string): { score: number; text: string; color: string } => {
    const score = [
        /.{12,}/.test(password),
        /[A-Z]/.test(password),
        /[a-z]/.test(password),
        /\d/.test(password),
        /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password),
    ].filter(Boolean).length;

    const strengthMap = {
        0: { text: 'Very Weak', color: '#d32f2f' },
        1: { text: 'Weak', color: '#ed6c02' },
        2: { text: 'Fair', color: '#ed6c02' },
        3: { text: 'Good', color: '#2e7d32' },
        4: { text: 'Strong', color: '#2e7d32' },
        5: { text: 'Very Strong', color: '#1976d2' },
    };

    return {
        score,
        text: strengthMap[score as keyof typeof strengthMap].text,
        color: strengthMap[score as keyof typeof strengthMap].color,
    };
};